/**
 * AssessorInbox — /assessor
 *
 * PR-Cluster-6-k (docs/ux-design-plan.md §10). Jordan's cross-
 * workspace landing surface — every engagement this assessor is
 * invited on, grouped by phase (Active / Read-only archive /
 * Expired / Revoked). Deep-links per row into the customer's
 * workspace-scoped shell so the assessor drops directly into
 * whichever engagement they're picking up next.
 *
 * NOT workspace-scoped — this page is the answer to "which
 * workspace do I open next?" so it can't depend on a workspace
 * already being in URL scope. Backend at
 * packages/cloudflare/src/routes/assessors.ts (assessorInbox
 * subrouter, mounted at /api/assessor outside workspaceContext).
 *
 * Signed-in-user only — the server's assessor session must hold
 * for the request to succeed. There's no "not an assessor" state
 * to render distinctly; the API returns an empty list and this
 * page shows the standard EmptyState.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, EmptyState, PageHeader, Spinner } from '@aigovops/design-system';
import { queryKeys } from '../lib/query-keys';
import { listAssessorInbox, type AssessorInboxRow } from '../lib/api';
import { fmtDate } from '../lib/format';

type EngagementPhase = 'active' | 'read_only' | 'archive_expired' | 'revoked';

/**
 * Mirrors `AssessorEngagementService.currentPhase()` on the server —
 * duplicating the logic client-side lets the inbox refresh phase
 * chips on the tick without a round-trip. Kept in sync with
 * AssessorEngagements.tsx's phaseOf().
 */
function phaseOf(e: AssessorInboxRow, now: Date = new Date()): EngagementPhase {
  if (e.revoked_at) return 'revoked';
  const nowMs = now.getTime();
  if (nowMs <= Date.parse(e.engagement_end)) return 'active';
  const archiveMs =
    Date.parse(e.engagement_end) + e.archive_window_days * 24 * 60 * 60 * 1000;
  if (nowMs <= archiveMs) return 'read_only';
  return 'archive_expired';
}

const PHASE_ORDER: Record<EngagementPhase, number> = {
  active: 0,
  read_only: 1,
  archive_expired: 2,
  revoked: 3,
};

export function AssessorInbox() {
  const inboxQuery = useQuery({
    queryKey: queryKeys.assessorInbox(),
    queryFn: () => listAssessorInbox(),
  });
  const inbox = {
    data: inboxQuery.data ?? null,
    error: inboxQuery.error?.message ?? null,
    loading: inboxQuery.isLoading,
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: '72rem' }}>
      <PageHeader
        title="Assessor inbox"
        subtitle="Every workspace you're currently engaged with, plus recently-ended engagements you can still read for the archive window."
      />
      {inbox.loading ? (
        <Spinner label="Loading engagements" />
      ) : inbox.error ? (
        <Alert variant="error">{inbox.error}</Alert>
      ) : !inbox.data || inbox.data.engagements.length === 0 ? (
        <EmptyState
          title="No engagements yet"
          description="When a workspace invites you as an assessor, the engagement lands here with a read-only session into their register + evidence."
        />
      ) : (
        <InboxList rows={inbox.data.engagements} />
      )}
    </div>
  );
}

// ---- List -----------------------------------------------------------

function InboxList({ rows }: { rows: AssessorInboxRow[] }) {
  // Sort by phase (active first) then engagement_end DESC so the
  // most-imminent-end active engagement is on top.
  const sorted = [...rows].sort((a, b) => {
    const phaseDelta = PHASE_ORDER[phaseOf(a)] - PHASE_ORDER[phaseOf(b)];
    if (phaseDelta !== 0) return phaseDelta;
    return Date.parse(b.engagement_end) - Date.parse(a.engagement_end);
  });
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {sorted.map((r) => (
        <InboxRow key={r.engagement_id} row={r} />
      ))}
    </div>
  );
}

// ---- Row ------------------------------------------------------------

function InboxRow({ row }: { row: AssessorInboxRow }) {
  const phase = phaseOf(row);
  const openHref = `/w/${row.workspace_slug}/app/dashboard`; // scope-check-ignore: workspace-scoped shape is dynamic + validated by workspaceContext
  const readOnly = phase === 'read_only';
  const closed = phase === 'archive_expired' || phase === 'revoked';
  return (
    <article
      style={{
        border: `1px solid ${phaseBorder(phase)}`,
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4) var(--space-5)',
        display: 'grid',
        gap: 'var(--space-3)',
        opacity: closed ? 0.7 : 1,
      }}
    >
      <header
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          alignItems: 'baseline',
          flexWrap: 'wrap',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-md)',
            fontWeight: 600,
            color: 'var(--text)',
            flex: 1,
            minWidth: 0,
          }}
        >
          {closed ? (
            <span style={{ color: 'var(--text-muted)' }}>{row.workspace_name}</span>
          ) : (
            <Link
              to={openHref}
              style={{
                color: 'inherit',
                textDecoration: 'underline',
                textDecorationColor: 'var(--border-strong)',
              }}
            >
              {row.workspace_name}
            </Link>
          )}
        </h2>
        <PhasePill phase={phase} />
      </header>

      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
          gap: 'var(--space-2) var(--space-4)',
          margin: 0,
          fontSize: 'var(--font-size-2xs)',
        }}
      >
        <MetaCell label="Firm">{row.assessor_firm}</MetaCell>
        <MetaCell label="Engagement">
          <time dateTime={row.engagement_start}>{fmtDate(row.engagement_start)}</time>
          {' → '}
          <time dateTime={row.engagement_end}>{fmtDate(row.engagement_end)}</time>
        </MetaCell>
        <MetaCell label="Archive window">{row.archive_window_days}d</MetaCell>
      </dl>

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <Counter
          label="Verdicts"
          value={row.active_annotation_count}
          tone={row.active_annotation_count > 0 ? 'accent' : 'muted'}
        />
        <Counter
          label="Open Q&A"
          value={row.open_qa_thread_count}
          tone={row.open_qa_thread_count > 0 ? 'warning' : 'muted'}
        />
        <Counter
          label="Active disputes"
          value={row.active_dispute_count}
          tone={row.active_dispute_count > 0 ? 'danger' : 'muted'}
        />
      </div>

      {row.revoked_reason ? (
        <p
          style={{
            margin: 0,
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--band-red)',
            borderRadius: 'var(--radius-card)',
            fontSize: 'var(--font-size-3xs)',
          }}
        >
          <strong style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Revoked:</strong>{' '}
          {row.revoked_reason}
        </p>
      ) : null}

      <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-4)' }}>
        <Link
          to={`/w/${row.workspace_slug}/app/assessor/workpaper/${row.engagement_id}`} // scope-check-ignore: dynamic workspace-scoped
          style={{
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-3xs)',
            fontWeight: 500,
            textDecoration: 'underline',
            textDecorationColor: 'var(--border-strong)',
          }}
        >
          Workpaper
        </Link>
        {!closed ? (
          <Link
            to={openHref}
            style={{
              color: 'var(--accent)',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            {readOnly ? 'Open (read-only)' : 'Open workspace'} →
          </Link>
        ) : null}
      </footer>
    </article>
  );
}

function MetaCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      <dt
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          margin: 0,
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
        {children}
      </dd>
    </div>
  );
}

// ---- Counters + phase pill ------------------------------------------

function Counter({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'muted' | 'accent' | 'warning' | 'danger';
}) {
  const toneMap: Record<
    'muted' | 'accent' | 'warning' | 'danger',
    { bg: string; fg: string; border: string }
  > = {
    muted: { bg: 'var(--surface-alt)', fg: 'var(--text-muted)', border: 'var(--border)' },
    accent: {
      bg: 'var(--surface-alt)',
      fg: 'var(--accent)',
      border: 'var(--border-strong)',
    },
    warning: {
      bg: 'var(--warning-bg)',
      fg: 'var(--warning-fg)',
      border: 'var(--band-amber)',
    },
    danger: {
      bg: 'var(--danger-bg)',
      fg: 'var(--danger-fg)',
      border: 'var(--band-red)',
    },
  };
  const t = toneMap[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: 'var(--radius-input)',
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 500,
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
      <span
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: t.fg,
          opacity: 0.85,
        }}
      >
        {label}
      </span>
    </span>
  );
}

function PhasePill({ phase }: { phase: EngagementPhase }) {
  const tone: Record<EngagementPhase, { bg: string; fg: string; label: string }> = {
    active: { bg: 'var(--success-bg)', fg: 'var(--success-fg)', label: 'Active' },
    read_only: {
      bg: 'var(--warning-bg)',
      fg: 'var(--warning-fg)',
      label: 'Read-only (archive)',
    },
    archive_expired: {
      bg: 'var(--surface-alt)',
      fg: 'var(--text-muted)',
      label: 'Expired',
    },
    revoked: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)', label: 'Revoked' },
  };
  const t = tone[phase];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
      }}
    >
      {t.label}
    </span>
  );
}

function phaseBorder(phase: EngagementPhase): string {
  switch (phase) {
    case 'active':
      return 'var(--band-green)';
    case 'read_only':
      return 'var(--band-amber)';
    case 'archive_expired':
      return 'var(--border)';
    case 'revoked':
      return 'var(--band-red)';
  }
}

